<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { useAdmin } from 'dashboard/composables/useAdmin';

import AgentLayout from 'dashboard/components-next/aiAgents/agent/AgentLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { useBrain } from './useBrain';

// Versions are immutable (D06); deploying a prior version IS the rollback.
// assignment_mode lives on the live deploy row (resolved tension #3) so a
// cutover flip never needs a republish.
const ASSIGNMENT_MODES = ['manual_only', 'all_new', 'round_robin_participant'];

const { t } = useI18n();
const store = useStore();
const { isAdmin } = useAdmin();
const { brainId, brain } = useBrain();

const versions = useMapGetter('aiAgentVersions/getRecords');
const deploys = useMapGetter('aiAgentDeploys/getRecords');
const versionUiFlags = useMapGetter('aiAgentVersions/getUIFlags');
const deployUiFlags = useMapGetter('aiAgentDeploys/getUIFlags');

const blockers = ref([]);

const sortedVersions = computed(() =>
  versions.value.slice().sort((a, b) => b.version_number - a.version_number)
);

const runtimeDeploy = computed(() =>
  deploys.value.find(deploy => deploy.target === 'runtime')
);

onMounted(() => {
  store.dispatch('aiAgentVersions/get', { brainId: brainId.value });
  store.dispatch('aiAgentDeploys/get', { brainId: brainId.value });
});

const formatDate = value =>
  value ? new Date(value).toLocaleString() : '';

const deployLabel = version => {
  const deployedNumber = runtimeDeploy.value?.deployed_version_number;
  if (deployedNumber && version.version_number < deployedNumber) {
    return t('AI_AGENTS.VERSIONS.ROLLBACK');
  }
  return t('AI_AGENTS.VERSIONS.DEPLOY');
};

const isDeployedVersion = version =>
  runtimeDeploy.value?.deployed_version_number === version.version_number;

const deployVersion = async version => {
  blockers.value = [];
  try {
    await store.dispatch('aiAgentDeploys/create', {
      brainId: brainId.value,
      deploy: { target: 'runtime', version_id: version.id },
    });
    useAlert(
      t('AI_AGENTS.VERSIONS.DEPLOY_SUCCESS', {
        version: version.version_number,
      })
    );
  } catch (error) {
    if (error.response?.status === 422 && error.response.data?.blockers) {
      // M12: readiness blockers — deploy stays blocked until both an
      // escalation team and an alert destination are configured.
      blockers.value = error.response.data.blockers;
    } else {
      useAlert(t('AI_AGENTS.VERSIONS.DEPLOY_ERROR'));
    }
  }
};

const updateAssignmentMode = async (deploy, event) => {
  try {
    await store.dispatch('aiAgentDeploys/update', {
      brainId: brainId.value,
      id: deploy.id,
      deploy: { assignment_mode: event.target.value },
    });
    useAlert(t('AI_AGENTS.VERSIONS.DEPLOYS.UPDATED'));
  } catch (error) {
    useAlert(t('AI_AGENTS.VERSIONS.DEPLOYS.UPDATE_ERROR'));
  }
};
</script>

<template>
  <AgentLayout v-if="brain.id" :brain="brain">
    <div class="flex flex-col gap-6 max-w-3xl">
      <div>
        <h2 class="text-lg font-medium text-n-slate-12">
          {{ t('AI_AGENTS.VERSIONS.HEADER') }}
        </h2>
        <p class="text-sm text-n-slate-11">
          {{ t('AI_AGENTS.VERSIONS.DESCRIPTION') }}
        </p>
      </div>

      <div
        v-if="blockers.length"
        class="flex flex-col gap-1 px-3 py-2 text-sm rounded-lg bg-n-ruby-3 text-n-ruby-11"
      >
        <span class="font-semibold">
          {{ t('AI_AGENTS.VERSIONS.BLOCKERS.TITLE') }}
        </span>
        <span v-for="blocker in blockers" :key="blocker">
          {{ t(`AI_AGENTS.VERSIONS.BLOCKERS.${blocker.toUpperCase()}`) }}
        </span>
      </div>

      <div
        v-if="versionUiFlags.fetchingList"
        class="flex justify-center py-6 text-n-slate-11"
      >
        <Spinner />
      </div>
      <p v-else-if="!sortedVersions.length" class="text-sm text-n-slate-11">
        {{ t('AI_AGENTS.VERSIONS.EMPTY') }}
      </p>
      <ul v-else class="flex flex-col gap-2 m-0 list-none">
        <li
          v-for="version in sortedVersions"
          :key="version.id"
          class="flex items-center gap-3 px-3 py-2 rounded-lg outline outline-1 outline-n-weak"
        >
          <span class="font-medium text-n-slate-12">
            {{
              t('AI_AGENTS.VERSIONS.VERSION', {
                version: version.version_number,
              })
            }}
          </span>
          <span class="text-xs text-n-slate-11">
            {{ formatDate(version.created_at) }}
          </span>
          <span class="flex-1 text-sm truncate text-n-slate-11">
            {{ version.change_notes || t('AI_AGENTS.VERSIONS.NO_NOTES') }}
          </span>
          <span class="text-xs font-mono text-n-slate-10">
            {{
              t('AI_AGENTS.VERSIONS.CHECKSUM', {
                checksum: (version.checksum || '').slice(0, 8),
              })
            }}
          </span>
          <span
            v-if="isDeployedVersion(version)"
            class="px-2 py-0.5 text-xs rounded-full bg-n-teal-3 text-n-teal-11"
          >
            {{ t('AI_AGENTS.VERSIONS.CURRENT') }}
          </span>
          <Button
            v-else-if="isAdmin"
            :label="deployLabel(version)"
            size="sm"
            variant="outline"
            :is-loading="deployUiFlags.creatingItem"
            @click="deployVersion(version)"
          />
        </li>
      </ul>

      <div class="flex flex-col gap-2">
        <h3 class="text-base font-medium text-n-slate-12">
          {{ t('AI_AGENTS.VERSIONS.DEPLOYS.TITLE') }}
        </h3>
        <p v-if="!deploys.length" class="text-sm text-n-slate-11">
          {{ t('AI_AGENTS.VERSIONS.DEPLOYS.EMPTY') }}
        </p>
        <div
          v-for="deploy in deploys"
          :key="deploy.id"
          class="flex flex-wrap items-center gap-3 px-3 py-2 text-sm rounded-lg outline outline-1 outline-n-weak text-n-slate-12"
        >
          <span class="font-medium">
            {{ t('AI_AGENTS.VERSIONS.DEPLOYS.TARGET') }}:
            {{
              t(`AI_AGENTS.ROSTER.CARD.TARGET.${deploy.target.toUpperCase()}`)
            }}
          </span>
          <span>
            {{ t('AI_AGENTS.VERSIONS.DEPLOYS.VERSION') }}:
            {{
              deploy.deployed_version_number
                ? t('AI_AGENTS.VERSIONS.VERSION', {
                    version: deploy.deployed_version_number,
                  })
                : '—'
            }}
          </span>
          <span>
            {{ t('AI_AGENTS.VERSIONS.DEPLOYS.SYNC_STATE') }}:
            {{
              t(
                `AI_AGENTS.ROSTER.CARD.SYNC_STATE.${deploy.sync_state.toUpperCase()}`
              )
            }}
          </span>
          <label
            v-if="isAdmin"
            class="flex items-center gap-2 ltr:ml-auto rtl:mr-auto"
          >
            {{ t('AI_AGENTS.VERSIONS.DEPLOYS.ASSIGNMENT_MODE') }}
            <select
              :value="deploy.assignment_mode"
              class="px-2 py-1 text-sm rounded-lg outline outline-1 outline-n-weak bg-n-solid-1"
              @change="updateAssignmentMode(deploy, $event)"
            >
              <option
                v-for="mode in ASSIGNMENT_MODES"
                :key="mode"
                :value="mode"
              >
                {{
                  t(
                    `AI_AGENTS.VERSIONS.DEPLOYS.ASSIGNMENT_MODES.${mode.toUpperCase()}`
                  )
                }}
              </option>
            </select>
          </label>
        </div>
      </div>
    </div>
  </AgentLayout>
  <div
    v-else
    class="flex items-center justify-center w-full h-full text-n-slate-11"
  >
    <Spinner />
  </div>
</template>
